import { useAuth } from '@workos-inc/authkit-react';
import { useEffect, useMemo, useState } from 'react';
import { useWhoami } from '../stores/org';

/**
 * Decodes a JWT payload without verification (client-side read of our own token).
 * Returns the payload object or null if decode fails.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    const payload = parts[1];
    if (payload === undefined) {
      return null;
    }
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function getFeatureFlagsFromToken(token: string): string[] {
  const payload = decodeJwtPayload(token);
  if (payload === null) {
    return [];
  }
  const flags = payload['feature_flags'];
  if (!Array.isArray(flags)) {
    return [];
  }
  return flags.filter((f): f is string => typeof f === 'string');
}

function useFeatureFlags(): { flags: string[] | null; isLoading: boolean } {
  const { getAccessToken, user } = useAuth();
  const orgId = useWhoami((s) => s.org.id);
  const [flags, setFlags] = useState<string[] | null>(null);

  useEffect(() => {
    if (user === null) {
      setFlags([]);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const token = await getAccessToken();
        if (cancelled || token === undefined) {
          return;
        }
        const featureFlags = getFeatureFlagsFromToken(token);
        if (!cancelled) {
          setFlags(featureFlags);
        }
      } catch {
        if (!cancelled) {
          setFlags([]);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [user, orgId, getAccessToken]);

  return { flags, isLoading: flags === null };
}

/**
 * Returns the set of feature flags enabled for the current user. Empty while
 * loading or unauthenticated. Use this when a caller needs to test several
 * flags at once without calling {@link useFeatureFlagEnabled} in a loop.
 */
export function useEnabledFeatureFlags(): ReadonlySet<string> {
  const { flags } = useFeatureFlags();

  return useMemo(() => new Set(flags ?? []), [flags]);
}

/**
 * Returns whether the given feature flag is enabled for the current user.
 * Reads the feature_flags claim from the WorkOS access token.
 * Returns false when not authenticated or while loading.
 */
export function useFeatureFlagEnabled(flagName: string): boolean {
  const { flags } = useFeatureFlags();

  return useMemo(
    () => (flags !== null ? flags.includes(flagName) : false),
    [flags, flagName]
  );
}

/**
 * Like useFeatureFlagEnabled but exposes loading state so callers can
 * distinguish "not loaded yet" from "flag is off".
 */
export function useFeatureFlagWithLoading(flagName: string): {
  enabled: boolean;
  isLoading: boolean;
} {
  const { flags, isLoading } = useFeatureFlags();

  const enabled = useMemo(
    () => (flags !== null ? flags.includes(flagName) : false),
    [flags, flagName]
  );

  return { enabled, isLoading };
}
